import * as React from "react";
import PropTypes from "prop-types";
// components
import palette from "../../theme/palette";
import { fCapitalizeFirst } from "../../utils/formatString";
import { TableRowWithDelete } from "../../components/table";
import DeletePopUp from "../../components/DeletePopUp";
import ClientAPI from "../../services/ClientService";

import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { displayError } from "../../utils/handleErrors";
import useLocales from "../../hooks/useLocales";
import { useSnackbar } from "notistack";
import { fCurrency, fPercent } from "../../utils/formatNumber";
import { anchorOrigin } from "../../utils/constants";
import { countryData } from "./CountryData";
import { formatToYYYYMonDD } from "../../utils/customC2CFormat";
import ConfigAPI from "../../services/ConfigServices";
import NavigationContext from "../../contexts/NavigationContext";
ClientTableRow.propTypes = {
  row: PropTypes.object,
  columns: PropTypes.array,
  selected: PropTypes.bool,
  onEditRow: PropTypes.func,
  onSelectRow: PropTypes.func,
  onItemClick: PropTypes.func,
  onDeleteRow: PropTypes.func
};

export default function ClientTableRow({
  row,
  columns,
  onEditRow,
  onItemClick,
  onDeleteRow
}) {
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const [isDeletePopUp, setDeletePopUp] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const onOpenDeletePopup = () => {
    setDeletePopUp(true);
  };

  const onCloseDeletePopup = () => {
    setDeletePopUp(false);
  };

  const handleDelete = () => {
    deleteItemRow();
  };

  async function deleteItemRow() {
    try {
      const response = await ClientAPI.DELETE(axiosPrivate, row.uuid);
      if (response.status >= 200 && response.status < 300) {
        onDeleteRow(row);
        onCloseDeletePopup();
        enqueueSnackbar(translate("message.delete"), { anchorOrigin });
      } else {
        displayError(enqueueSnackbar, {
          message: translate("error.deleteFail"),
          anchorOrigin
        });
      }
    } catch (error) {
      displayError(enqueueSnackbar, error, { anchorOrigin });
    }
  }

  return (
    <>
      <TableRowWithDelete
        row={row}
        columns={columns}
        onEditRow={onEditRow}
        onDeleteRow={onOpenDeletePopup}
        onItemClick={onItemClick}
      />

      {isDeletePopUp && (
        <DeletePopUp onClose={onCloseDeletePopup} onDelete={handleDelete} />
      )}
    </>
  );
}
const labelsConfig = JSON.parse(localStorage.getItem("labelsConfig") || "{}");
export const Client_Table_Header = (translate, isClientList) => {
  const labelsConfig = JSON.parse(localStorage.getItem("labelsConfig") || "{}");
  const listingLabels = isClientList
    ? labelsConfig.clientManagement.client.listing
    : labelsConfig.budgetManagement.budget.listing;
  return [
    {
      id: "name",
      label: isClientList ? listingLabels.clientName : listingLabels.budgetName,
      align: "left",
      format: (client) => {
        return client?.name ? fCapitalizeFirst(client.name) : "--";
      }
    },
    {
      id: "country",
      label: isClientList ? listingLabels.country : listingLabels.country, // future-proof for config changes
      align: "left",
      format: (client) => {
        const getCountryName = countryData.find(
          (country) => country.country_id === client?.country
        );
        return getCountryName?.country_name || "--";
      }
    },
    {
      id: isClientList ? "client_created_by" : "budget_created_by",
      label: isClientList
        ? listingLabels.clientCreatedBy
        : listingLabels.budgetCreatedBy,
      align: "left",
      format: (client) => {
        const val = client.client_created_by;
        return val ? fCapitalizeFirst(val) : "N/A";
      }
    }
    // Add more columns as needed, using config
  ];
};

export const calculateMarginsFromResource = (resourceArr) => {
  if (!Array.isArray(resourceArr) || resourceArr.length === 0) {
    return { grossMargin: 0, netMargin: 0 };
  }
  let aggregatedBR = 0;
  let aggregatedPR = 0;
  resourceArr.forEach((res) => {
    // Prefer pay_rate_info.billrate/payrate if available, else fallback to pay_rate fields
    const bill =
      Number(res.pay_rate_info?.billrate ?? res.bill_rate ?? res.pay_rate) || 0;
    const pay = Number(res.pay_rate_info?.payrate ?? res.pay_rate) || 0;
    const n = Number(res.num_of_resources) || 1;
    const h = Number(res.total_estimation_hour) || 0;
    aggregatedBR += bill * n * h;
    aggregatedPR += pay * n * h;
  });
  let gm = 0;
  let nm = 0;

  let nmOverhead = parseFloat(process.env.REACT_APP_NM_OVERHEAD);

  if (aggregatedBR > 0) {
    gm = ((aggregatedBR - aggregatedPR) / aggregatedBR) * 100;

    if (nmOverhead !== undefined) {
      nm = ((aggregatedBR - aggregatedPR * nmOverhead) / aggregatedBR) * 100;
    } else {
      console.log("NM Overhead not defined, netMargin remains 0.");
    }
  } else {
    console.log("aggregatedBR is 0, skipping margin calculations.");
  }

  const result = {
    grossMargin: Number(gm.toFixed(2)),
    netMargin: Number(nm.toFixed(2))
  };

  return result;
};

export const Estimation_Table_Header = (translate, config = {}) => {
  // const labelsConfig = JSON.parse(localStorage.getItem("labelsConfig") || "{}");
  const isClientTab = localStorage.getItem("selectedTab") === "client";

  // Determine which part of the config to use based on tab
  // For client tab: clientManagement.clientEstimation.listing
  // For budget tab: budgetManagement.budgetEstimation.listing
  const estimationLabels = isClientTab
    ? labelsConfig?.clientManagement?.clientEstimation?.listing
    : labelsConfig?.budgetManagement?.budgetEstimation?.listing;

  return [
    {
      id: "name",
      label: estimationLabels?.estimationName || translate("Estimation Name"),
      align: "left",
      format: (row) => {
        return row?.name ? fCapitalizeFirst(row.name) : "--";
      }
    },
    {
      id: "resources",
      label: estimationLabels?.resources || translate("Resources"),
      align: "center",
      format: (row) => {
        const totalResources =
          row.resource?.reduce(
            (sum, resourceEntry) => sum + (resourceEntry.num_of_resources || 0),
            0
          ) ?? 0;
        return `${totalResources} ${translate("RESOURCES")}`;
      }
    },
    {
      id: "TOTAL_GM_COMPANY",
      label:
        estimationLabels?.totalBillAmount ||
        translate("Total Bill Amount(Customer)"),
      align: "center",
      format: (row) => {
        return row?.company_avg_cost !== undefined &&
          row?.company_avg_cost !== null
          ? fCurrency(row?.company_avg_cost)
          : "--";
      }
    },

    {
      id: "total_gm_market",
      label: estimationLabels?.grossMargin || translate("Gross Margin(%)"),
      align: "center",
      format: (row) => {
        return row?.aggregated_gross_margin != null
          ? row.aggregated_gross_margin.toFixed(2)
          : "--";
      }
    },
    {
      id: "total_nm_market",
      label: "Net Margin(%)",
      align: "center",
      format: (row) => {
        return row?.aggregated_net_margin != null
          ? row.aggregated_net_margin.toFixed(2)
          : "--";
      }
    },
    {
      id: "date",
      label: estimationLabels?.estimationDate || translate("Estimation Date"),
      align: "left",
      format: (row) => {
        return formatToYYYYMonDD(row?.date_created);
      }
    }
  ];
};
